import React, { ReactNode, useEffect, useContext } from "react";
import { SimpleApi } from "../utils/simple-api/simple-api";
import { AnalyticsContext } from "../utils/analytics/analytics-context";
import { PDF_URL } from "../constants/api";

const TOKEN_KEY = "cc-t";

enum AuthStatus {
  UNAUTHENTICATED,
  AUTHENTICATED,
  PENDING,
  ERROR,
}

interface AuthContextInnerState {
  status: AuthStatus;
  token?: string;
  error?: {};
}

interface AuthContextState extends AuthContextInnerState {
  login: (username: string, password: string) => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextState>({
  status: AuthStatus.UNAUTHENTICATED,
  login: () => {},
  logout: () => {},
});

let api: SimpleApi = new SimpleApi();
let pdfApi: SimpleApi = new SimpleApi(PDF_URL);

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): React.ReactElement {
  const tokenInStorage = sessionStorage.getItem(TOKEN_KEY) as string;
  const { track } = useContext(AnalyticsContext);

  const [state, setState] = React.useState<AuthContextInnerState>({
    status: AuthStatus.PENDING,
    token: tokenInStorage,
  });

  useEffect(() => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (t) {
      api.setToken(t);
      const checkAuth = async (): Promise<void> => {
        const { status } = await api.getAuthenticatedData();
        if (status === 200) {
          setState({ token: t, status: AuthStatus.AUTHENTICATED });
        } else {
          sessionStorage.removeItem(TOKEN_KEY);
          setState({ status: AuthStatus.UNAUTHENTICATED, token: undefined });
        }
      };
      checkAuth();
    } else {
      setState({ status: AuthStatus.UNAUTHENTICATED });
    }
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    setState({ status: AuthStatus.PENDING });

    const { status, data } = await api.login(username, password);
    if (status === 200) {
      const { token } = data as { token: string };
      sessionStorage.setItem(TOKEN_KEY, token);
      setState({ token, status: AuthStatus.AUTHENTICATED });
      track("login", { user: username });
    } else {
      setState({ status: AuthStatus.ERROR });
      track("login_fail", { user: username });
    }
  };
  const logout = (): void => {
    sessionStorage.removeItem(TOKEN_KEY);
    setState({ status: AuthStatus.UNAUTHENTICATED, token: undefined });
  };
  const contextState = { ...state, login, logout };
  return (
    <AuthContext.Provider value={contextState}>{children}</AuthContext.Provider>
  );
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
}

export function useAuthState(): AuthContextState & AuthState {
  const state = React.useContext(AuthContext);
  const isSuccess = state.status === AuthStatus.AUTHENTICATED;
  const isAuthenticated = isSuccess; // state.user && isSuccess;
  const isLoading = state.status === AuthStatus.PENDING;
  return {
    ...state,
    isAuthenticated,
    isLoading,
  };
}

interface Api {
  api: SimpleApi;
  pdfApi: SimpleApi;
}

export function useApi(): Api {
  const { token, logout } = useAuthState();

  api = new SimpleApi(undefined, token, logout);
  pdfApi = new SimpleApi(PDF_URL, token, logout);

  return {
    api,
    pdfApi,
  };
}
